import moment from "moment";
import { getOccurrencesForJob } from "./getOccurrencesForJob";

// ✅ Date-only key (matches Home.js behavior)
const toDateKey = (v) => {
  if (!v) return "";

  // Firestore Timestamp
  if (typeof v === "object" && v?.seconds) {
    return moment(v.seconds * 1000).format("YYYY-MM-DD");
  }

  // strings: keep only YYYY-MM-DD
  if (typeof v === "string") {
    const s = v.slice(0, 10);
    return moment(s, "YYYY-MM-DD", true).isValid() ? s : "";
  }

  // Date
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const isRecurringJob = (job) =>
  job?.recurrence?.type && job.recurrence.type !== "none";

/**
 * ✅ NEW: materials for a horizon (default 1 week, set to 4 for 4 weeks out)
 * - One-time jobs: uses estimatedDays overlap logic (your original behavior)
 * - Recurring jobs: expands occurrences using getOccurrencesForJob()
 */
export const materialsNeededForWeeks = (jobs, weeksAhead = 1) => {
  // We want a *forward-looking* range: today → today + weeksAhead
  const rangeStart = moment().startOf("day");
  const rangeEnd = moment().add(weeksAhead, "weeks").endOf("day");

  const out = [];

  jobs.forEach((job) => {
    const materials = job?.jobPlan?.materials || [];
    if (!materials.length) return;

    // match your Home.js behavior: ignore Invoice Sent
    if (job.status === "Invoice Sent") return;

    // ---------- ONE-TIME (your overlap logic, but for a forward range) ----------
    if (!isRecurringJob(job)) {
      // skip completed one-time jobs
      if (job.status === "Completed") return;

      const startKey = toDateKey(job.startDate);
      if (!startKey) return;

      const jobStart = moment(startKey, "YYYY-MM-DD");

      // If estimatedDays exists, treat job as spanning multiple days (your original logic)
      const estDays = Number(job.jobPlan?.estimatedDays || 0);
      const jobEnd =
        estDays && estDays > 1
          ? jobStart.clone().add(estDays - 1, "days")
          : jobStart;

      // overlaps forward range?
      const overlaps =
        jobStart.isSameOrBefore(rangeEnd, "day") &&
        jobEnd.isSameOrAfter(rangeStart, "day");

      if (!overlaps) return;

      // push materials (include ids so Ordered/Received can update)
      materials.forEach((mat, idx) => {
        out.push({
          jobId: job.id,
          materialIndex: idx,
          siteName: job.siteName,
          wo: job.wo,
          startDate: startKey, // job's start date
          ...mat,
        });
      });

      return;
    }

    // ---------- RECURRING (occurrence expansion) ----------
    const seriesStartKey = toDateKey(job.startDate);
    if (!seriesStartKey) return;

    const occStart = moment(seriesStartKey, "YYYY-MM-DD");

    // get occurrences from series start through rangeEnd
    const rawDates = getOccurrencesForJob(job, occStart, rangeEnd) || [];

    const excluded = new Set(
      (job.excludedDates || []).map(toDateKey).filter(Boolean)
    );

    const completed = new Set(
      (job.completedDates || []).map(toDateKey).filter(Boolean)
    );

    rawDates.forEach((d) => {
      const occKey = toDateKey(d);
      if (!occKey) return;

      const occDate = moment(occKey, "YYYY-MM-DD");
      if (!occDate.isBetween(rangeStart, rangeEnd, "day", "[]")) return;

      // skip cancelled/completed occurrences
      if (excluded.has(occKey)) return;
      if (completed.has(occKey)) return;

      materials.forEach((mat, idx) => {
        out.push({
          jobId: job.id,
          materialIndex: idx,
          siteName: job.siteName,
          wo: job.wo,
          startDate: occKey, // ✅ the occurrence date (so you can group by date later)
          ...mat,
        });
      });
    });
  });

  // sort: soonest first, then site, then material name
  out.sort((a, b) => {
    const da = a.startDate || "";
    const db = b.startDate || "";
    if (da !== db) return da.localeCompare(db);
    const sa = a.siteName || "";
    const sb = b.siteName || "";
    if (sa !== sb) return sa.localeCompare(sb);
    return (a.name || "").localeCompare(b.name || "");
  });

  return out;
};

/**
 * ✅ BACKWARD COMPAT:
 * This keeps your existing usage working.
 * "This week" = next 1 week horizon.
 */
export const materialsNeededThisWeek = (jobs) => {
  return materialsNeededForWeeks(jobs, 1);
};
